/**
 * The dev build's frame around the app: the chat, and the «Отладка» drawer the
 * header's button opens (owner, 07.10.2026 — INV-PH-64 revised: the chat
 * screens are the Figma's, and diagnostics/position live behind one button
 * instead of two permanent columns).
 *
 * **The wrapper is always the same element in a dev build**, open or closed.
 * Swapping `<>{children}</>` for `<div>{children}</div>` on open would
 * re-parent `<App/>` and remount it — a streaming turn and every local state
 * with it. Prod renders the children bare and has no drawer at all.
 *
 * Wide: the drawer is a column beside the chat, so a turn can be watched while
 * it runs. Narrow: it covers the screen and closes with ✕ or Escape.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '../store/sessionStore';
import ErrorBoundary from '../app/ErrorBoundary';
import LocationPanel from '../location/LocationPanel';
import PointChooser from '../location/PointChooser';
import DevTools from './DevTools';
import SettingsPanel from './SettingsPanel';
import { DEV_TOOLS } from './diagnostics';
import { DEV_TAB_LABELS, devTabsFor, useDevPanel } from './devPanelStore';
import { cn } from '../lib/cn';

export default function DevWorkspace({ children }: { children: ReactNode }) {
  const userId = useSession((s) => s.userId);
  const assistantId = useSession((s) => s.assistantId);
  const open = useDevPanel((s) => s.open);
  const chosen = useDevPanel((s) => s.tab);

  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') useDevPanel.getState().close();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open]);

  if (!DEV_TOOLS) return <>{children}</>;
  const tabs = devTabsFor(assistantId);
  const tab = tabs.includes(chosen) ? chosen : tabs[0]!;

  return (
    <div className={cn('dev-workspace', open && 'dev-workspace--open')}>
      <div className="dev-chat">{children}</div>
      {open ? (
        <aside className="dev-drawer" aria-label="Отладка">
          <div className="dev-drawer__bar">
            <div className="dev-drawer__tabs" role="tablist" aria-label="Разделы отладки">
              {tabs.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={id === tab}
                  className="dev-drawer__tab"
                  onClick={() => useDevPanel.getState().show(id)}
                >
                  {DEV_TAB_LABELS[id]}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="glass-btn glass-btn--small"
              onClick={() => useDevPanel.getState().close()}
              title="Закрыть отладку"
            >
              <span className="mask-icon mask-icon--close" aria-hidden="true" />
              <span className="visually-hidden">Закрыть отладку</span>
            </button>
          </div>
          <div className="dev-panel" role="tabpanel" aria-label={DEV_TAB_LABELS[tab]}>
            {tab === 'diagnostics' ? (
              <ErrorBoundary label="diagnostics"><DevTools /></ErrorBoundary>
            ) : tab === 'location' ? (
              <ErrorBoundary label="location">
                <section className="dev-location" aria-label="Местоположение">
                  <LocationPanel key={userId} />
                  <PointChooser />
                </section>
              </ErrorBoundary>
            ) : (
              <ErrorBoundary label="settings"><SettingsPanel /></ErrorBoundary>
            )}
          </div>
        </aside>
      ) : null}
    </div>
  );
}
